/**
 * Testo del post social dei verbali (piano 3, task 7): controlli puri del sito.
 *
 * Speculare a `backend/utils/verbaliSocialTesto.js` (stesse costanti, stessa `lunghezzaPesataX`,
 * stesso `contieneUrl`): i casi condivisi sono in `shared/fixtures/verbaliSocialX.casi.json`,
 * letti dai test di entrambi. Una modifica va fatta nei due file e nei casi.
 */

export const MAX_TESTO_SOCIAL = 2200;
export const MIN_TESTO_SOCIAL = 20;
export const MAX_X = 280;
export const MIN_X = 10;
export const PREFISSO_PROVA = '[PROVA STAGING] ';

/** Testo esatto della spunta obbligatoria sulle foto (registrata in `social.consensoFotoAt`). */
export const TESTO_CONSENSO_FOTO =
  'Le persone riconoscibili nelle foto sono d\'accordo con la pubblicazione sui social dell\'associazione';

// Intervalli di peso 1 di X (twitter-text v3, `ranges` della configurazione predefinita).
const INTERVALLI_LEGGERI: Array<[number, number]> = [[0, 4351], [8192, 8205], [8208, 8223], [8242, 8247]];
const RE_EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;

// Ripiego senza `Intl.Segmenter` (browser vecchi): grafemi approssimati unendo al code point
// precedente segni combinanti, U+200D (e il code point che lo segue), selettori di variante
// U+FE0E/U+FE0F, modificatori di carnagione, tag delle bandiere e la coppia di indicatori regionali.
// Copre le emoji composte dei casi condivisi; per sequenze più rare (es. cluster complessi degli
// script indiani) il conteggio può differire da quello di `Intl.Segmenter` (il server rivalida).
const RE_ESTENSIONE = /[\p{M}\u200D\uFE0E\uFE0F\u{1F3FB}-\u{1F3FF}\u{E0020}-\u{E007F}]/u;
const RE_INDICATORE = /\p{Regional_Indicator}/u;

function grafemiApprossimati(testo: string): string[] {
  const elenco: string[] = [];
  let dopoZwj = false;
  let indicatoreAperto = false;
  for (const c of Array.from(testo)) {
    const indicatore = RE_INDICATORE.test(c);
    if (elenco.length && (dopoZwj || RE_ESTENSIONE.test(c) || (indicatore && indicatoreAperto))) {
      elenco[elenco.length - 1] += c;
      indicatoreAperto = false;
    } else {
      elenco.push(c);
      indicatoreAperto = indicatore;
    }
    dopoZwj = c === '\u200D';
  }
  return elenco;
}

let segmentatore: any = null;
function grafemi(testo: string): string[] {
  const Segmenter = typeof Intl !== 'undefined' ? (Intl as any).Segmenter : undefined;
  if (typeof Segmenter === 'function') {
    if (!segmentatore) segmentatore = new Segmenter('it', { granularity: 'grapheme' });
    return Array.from(segmentatore.segment(testo) as Iterable<{ segment: string }>, (s) => s.segment);
  }
  return grafemiApprossimati(testo);
}

function pesoCodePoint(cp: number): number {
  return INTERVALLI_LEGGERI.some(([da, a]) => cp >= da && cp <= a) ? 1 : 2;
}

/** Lunghezza di un testo secondo le regole di X (testo in NFC, pesi 1/2 per code point, emoji 2). */
export function lunghezzaPesataX(testo: string | null | undefined): number {
  if (typeof testo !== 'string' || !testo) return 0;
  let totale = 0;
  for (const grafema of grafemi(testo.normalize('NFC'))) {
    if (RE_EMOJI.test(grafema)) {
      totale += 2;
    } else {
      for (const carattere of Array.from(grafema)) totale += pesoCodePoint(carattere.codePointAt(0) as number);
    }
  }
  return totale;
}

const LETTERA = '\\p{L}\\p{N}';
const RE_URL_ESPLICITO = /\bhttps?:\/\/|\bwww\./i;
// Niente lookbehind `(?<!…)`: Safari/iOS prima della 16.4 lo rifiuta al caricamento del modulo.
// Il gruppo iniziale `(?:^|[^…])` consuma un carattere, ma con `.test()` il risultato è lo stesso.
const RE_DOMINIO = new RegExp(
  `(?:^|[^${LETTERA}_-])[${LETTERA}-]+(?:\\.[${LETTERA}-]+)*\\.(?:it|com|org|net|eu|info|gov|edu)(?![${LETTERA}_-])`,
  'iu'
);

/** `https?://`, `www.` o un dominio `parola.it|com|org|net|eu|info|gov|edu` come parola intera. */
export function contieneUrl(testo: string | null | undefined): boolean {
  if (typeof testo !== 'string' || !testo) return false;
  return RE_URL_ESPLICITO.test(testo) || RE_DOMINIO.test(testo);
}
